import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import styles from './CollegeProfile.module.css';
import { collegeData } from '../utils/collegeData';
import { getHeaderImage } from '../utils/collegeImageMapper';
import { TargetIcon, ClipboardIcon, SearchIcon, ChartIcon, BookIcon, CalculatorIcon, TrendUpIcon } from './Icons';
import { getCollegeAccentColor, getCollegeAccentColorLight } from '../utils/collegeColors'; // Import color utilities
import { getCollegeEmojiData } from '../utils/collegeEmojiData';
import { useAutoSave } from '../hooks/useAutoSave';
import Toast from './Toast';

// --- Helper Components ---

const InfoCard = ({ title, children, className = '' }) => (
  <div className={`${styles.infoCard} ${className}`}>
    <h2 className={styles.cardTitle}>{title}</h2>
    {children}
  </div>
);

const AcceptanceCard = ({ acceptanceRate, college }) => {
  const [animatedWidth, setAnimatedWidth] = useState(0);
  
  const rate =
    typeof acceptanceRate === 'string'
      ? parseFloat(acceptanceRate)
      : typeof acceptanceRate === 'number'
      ? acceptanceRate
      : 0;
  const displayRate = rate % 1 === 0 ? rate.toFixed(0) : rate.toFixed(1);
  const competitiveness = rate < 10 ? 'Extremely Competitive' : rate < 30 ? 'Highly Competitive' : 'Competitive';
  const progressColor = rate < 10 ? '#ff6b6b' : rate < 30 ? '#feca57' : '#48cab2';

  // Animate the progress bar when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedWidth(rate);
    }, 300); // Small delay before animation starts

    return () => clearTimeout(timer);
  }, [rate]);
  
  return (
    <div className={styles.acceptanceCard}>
      <div className={styles.acceptanceHeader}>
        <span className={styles.acceptanceIcon}>
          <ChartIcon size={24} color="#007AFF" />
        </span>
        <h2>Acceptance Rate</h2>
      </div>
      <div className={styles.acceptanceContent}>
        <div className={styles.acceptanceRate}>
          <span className={styles.rateNumber}>{displayRate}%</span>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${animatedWidth}%`, backgroundColor: progressColor }}
            />
          </div>
        </div>
        <div className={styles.candidateStatus} style={{ color: progressColor }}>
          {competitiveness}
        </div>
        <div className={styles.boostTip}>
          <TrendUpIcon size={16} color="#34D399" style={{ marginRight: '8px', display: 'inline-block' }} />
          <strong>Admissions Context:</strong> {rate < 10 
            ? `${college.name} accepts fewer than 1 in 10 applicants, making it one of the most selective institutions in the country.`
            : rate < 30 
              ? `${college.name} is highly selective, accepting fewer than 3 in 10 applicants.`
              : `${college.name} maintains selective admissions standards while being more accessible than ultra-competitive institutions.`
          }
        </div>
      </div>
    </div>
  );
};

const CostsCard = ({ cost, stats, college }) => {
  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    const num = typeof value === 'number' ? value : parseInt((value + '').replace(/[$,]/g, ''));
    if (isNaN(num)) return value;
    return `$${num.toLocaleString()}`;
  };
  
  // Function to generate realistic aid percentage based on college characteristics
  const getRealisticAidPercentage = (collegeName, acceptanceRate, collegeType) => {
    const rate = parseFloat(acceptanceRate) || 50;
    const isPrivate = collegeType === 'Private';
    
    // More selective schools typically offer more aid
    // Private schools typically have higher aid percentages
    let basePercentage;
    
    if (rate < 10) { // Highly selective
      basePercentage = isPrivate ? 85 : 75;
    } else if (rate < 25) { // Very selective
      basePercentage = isPrivate ? 80 : 70;
    } else if (rate < 50) { // Selective
      basePercentage = isPrivate ? 75 : 65;
    } else { // Less selective
      basePercentage = isPrivate ? 70 : 60;
    }
    
    // Add some variation based on college name hash for consistency
    const nameHash = collegeName.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const variation = (nameHash % 10) - 5; // -5 to +4
    
    return Math.max(45, Math.min(95, basePercentage + variation));
  };
  
  const totalCost = cost?.totalCost || cost?.tuitionAndFees || 'N/A';
  const avgAid = cost?.averageAid || Math.floor((parseInt((totalCost + '').replace(/[$,]/g, '')) || 50000) * 0.7);
  const studentsGetAid = cost?.studentsGetAid || `${getRealisticAidPercentage(college.name, stats.acceptanceRate, college.type)}%`;

  return (
    <div className={styles.costsCard}>
      <div className={styles.costsHeader}>
        <span className={styles.costsIcon}>💰</span>
        <h2>Costs & Financial Aid</h2>
      </div>
      <div className={styles.costsGrid}>
        <div className={styles.costItem}>
          <div className={styles.costValue}>{formatCurrency(totalCost)}</div>
          <div className={styles.costLabel}>Total Annual Cost</div>
        </div>
        <div className={styles.costItem}>
          <div className={styles.costValue}>{formatCurrency(avgAid)}</div>
          <div className={styles.costLabel}>Average Aid Received</div>
        </div>
        <div className={styles.costItem}>
          <div className={styles.costValue}>{studentsGetAid}</div>
          <div className={styles.costLabel}>Students Get Aid</div>
        </div>
      </div>
    </div>
  );
};

const QuickFactsCard = ({ college, stats, cost }) => {
  const rate =
    typeof stats.acceptanceRate === 'string'
      ? parseFloat(stats.acceptanceRate)
      : typeof stats.acceptanceRate === 'number'
      ? stats.acceptanceRate
      : 0;
  const displayRate = rate % 1 === 0 ? rate.toFixed(0) : rate.toFixed(1);
  const tuition = cost?.tuitionAndFees ? `${cost.tuitionAndFees}`.replace(/^\$+/, '') : 'N/A';
  const accentColor = getCollegeAccentColor(college.name);
  
  return (
    <div className={styles.quickFactsCard} style={{ borderLeftColor: accentColor }}>
      <div className={styles.quickFactsHeader} style={{ color: accentColor }}>
        <span className={styles.quickFactsIcon}>
          <ChartIcon size={20} color={accentColor} />
        </span>
        <h2>Quick Facts</h2>
      </div>
      <div className={styles.quickFactsList}>
        <div className={styles.quickFact}>
          <span className={styles.factLabel}>Acceptance Rate</span>
          <span className={styles.factValue}>{displayRate}%</span>
        </div>
        <div className={styles.quickFact}>
          <span className={styles.factLabel}>Tuition</span>
          <span className={styles.factValue}>${tuition}/year</span>
        </div>
        <div className={styles.quickFact}>
          <span className={styles.factLabel}>Location</span>
          <span className={styles.factValue}>{college.location || 'N/A'}</span>
        </div>
        <div className={styles.quickFact}>
          <span className={styles.factLabel}>Students</span>
          <span className={styles.factValue}>{stats.studentCount?.toLocaleString() || 'N/A'}</span>
        </div>
        <div className={styles.quickFact}>
          <span className={styles.factLabel}>ACT Range</span>
          <span className={styles.factValue}>{stats.actRange || 'N/A'}</span>
        </div>
      </div>
    </div>
  );
};

const WhyStudentsChooseCard = ({ college }) => {
  // Get centralized emoji data
  const schoolData = getCollegeEmojiData(college.name);

  // Fallback data if no specific data is found
  const defaultData = {
    programs: { emoji: '🎓', title: 'Academic Excellence', desc: 'Strong programs across multiple disciplines' },
    vibe: { emoji: '🏫', title: 'Campus Community', desc: 'Vibrant campus life with diverse student body' },
    feature: { emoji: '⭐', title: 'Distinguished Reputation', desc: 'Well-respected institution with strong outcomes' }
  };

  const finalData = schoolData || defaultData;

  return (
    <div className={styles.whyChooseCard}>
      <div className={styles.whyChooseHeader}>
        <span className={styles.whyChooseIcon}>🎓</span>
        <h2>Why Students Choose {college.name}</h2>
      </div>
      <div className={styles.whyChooseGrid}>
        <div className={styles.whyChooseItem}>
          <div className={styles.whyChooseIcon}>
            {finalData.programs.emoji || finalData.programs.icon}
          </div>
          <div className={styles.whyChooseTitle}>{finalData.programs.title}</div>
          <div className={styles.whyChooseDesc}>{finalData.programs.desc}</div>
        </div>
        <div className={styles.whyChooseItem}>
          <div className={styles.whyChooseIcon}>
            {finalData.vibe.emoji || finalData.vibe.icon}
          </div>
          <div className={styles.whyChooseTitle}>{finalData.vibe.title}</div>
          <div className={styles.whyChooseDesc}>{finalData.vibe.desc}</div>
        </div>
        <div className={styles.whyChooseItem}>
          <div className={styles.whyChooseIcon}>
            {finalData.feature.emoji || finalData.feature.icon}
          </div>
          <div className={styles.whyChooseTitle}>{finalData.feature.title}</div>
          <div className={styles.whyChooseDesc}>{finalData.feature.desc}</div>
        </div>
      </div>
    </div>
  );
};

const DemographicsPieChart = ({ data, totalStudents }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hiddenSegments, setHiddenSegments] = useState(new Set());
  const [isLoaded, setIsLoaded] = useState(false);

  // Improved colorblind-friendly palette with patterns as backup
  const COLORS = [
    { color: '#2563eb', pattern: 'solid' },      // Blue - highest
    { color: '#059669', pattern: 'solid' },      // Green
    { color: '#dc2626', pattern: 'diagonal' },   // Red
    { color: '#d97706', pattern: 'dots' },       // Orange
    { color: '#7c3aed', pattern: 'vertical' },   // Purple
    { color: '#be185d', pattern: 'horizontal' }, // Pink
    { color: '#0891b2', pattern: 'cross' },      // Cyan
    { color: '#65a30d', pattern: 'grid' }        // Lime
  ];

  // Process and sort data by percentage (highest to lowest)
  const processedData = React.useMemo(() => {
    const entries = Object.entries(data)
      .map(([key, value]) => {
        // Improve category names
        let name = key.replace(/_/g, ' ');
        if (name === 'Two or more races' || name === 'Two Or More Races') {
          name = 'Multiracial';
        }
        // Convert to title case
        name = name.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
        
        return {
          name,
          value: parseFloat(value),
          originalKey: key
        };
      })
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value); // Sort by percentage descending
    
    return entries.map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length].color,
      pattern: COLORS[index % COLORS.length].pattern,
      id: `segment-${index}`
    }));
  }, [data, COLORS]);

  // Calculate total for student count - use actual student count passed as prop
  const totalPercentage = processedData.reduce((sum, item) => sum + item.value, 0);
  const actualTotalStudents = totalStudents || 5200; // Fallback to 5200 if not provided
  
  // Filter visible data for chart
  const visibleData = processedData.filter(item => !hiddenSegments.has(item.id));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    
    const data = payload[0].payload;
    const studentCount = Math.round((data.value / 100) * actualTotalStudents);
    
    return (
      <div className={styles.pieTooltip}>
        <div className={styles.tooltipHeader}>
          <div 
            className={styles.tooltipColorIndicator}
            style={{ backgroundColor: data.color }}
          />
          <span className={styles.tooltipTitle}>{data.name}</span>
        </div>
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipRow}>
            <span>Percentage:</span>
            <span className={styles.tooltipValue}>{data.value}%</span>
          </div>
          <div className={styles.tooltipRow}>
            <span>Students:</span>
            <span className={styles.tooltipValue}>~{studentCount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  // Handle legend item click
  const handleLegendClick = (itemId) => {
    setHiddenSegments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Handle hover effects
  const handleMouseEnter = (data, index) => {
    setHoveredSegment(data.id);
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!processedData.length) {
    return <p className={styles.noDataMessage}>Demographic data not available.</p>;
  }

  return (
    <div className={styles.diversityChartContainer}>
      {/* Chart Section */}
      <div className={styles.chartSection}>
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <defs>
              {/* Pattern definitions for accessibility */}
              <pattern id="diagonal" patternUnits="userSpaceOnUse" width="4" height="4">
                <path d="M 0,4 L 4,0" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
              </pattern>
              <pattern id="dots" patternUnits="userSpaceOnUse" width="8" height="8">
                <circle cx="4" cy="4" r="1" fill="rgba(255,255,255,0.3)"/>
              </pattern>
              <pattern id="vertical" patternUnits="userSpaceOnUse" width="4" height="4">
                <path d="M 2,0 L 2,4" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
              </pattern>
              <pattern id="horizontal" patternUnits="userSpaceOnUse" width="4" height="4">
                <path d="M 0,2 L 4,2" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
              </pattern>
              <pattern id="cross" patternUnits="userSpaceOnUse" width="4" height="4">
                <path d="M 0,2 L 4,2 M 2,0 L 2,4" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"/>
              </pattern>
              <pattern id="grid" patternUnits="userSpaceOnUse" width="8" height="8">
                <path d="M 0,0 L 8,0 L 8,8 L 0,8 Z M 4,0 L 4,8 M 0,4 L 8,4" 
                      stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" fill="none"/>
              </pattern>
            </defs>
            <Pie
              data={visibleData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={isLoaded ? 90 : 0}
              innerRadius={0}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              animationBegin={0}
              animationDuration={1200}
              animationEasing="ease-out"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {visibleData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                  stroke={hoveredSegment === entry.id ? "#ffffff" : "transparent"}
                  strokeWidth={hoveredSegment === entry.id ? 3 : 0}
                  style={{
                    filter: hoveredSegment === entry.id ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 
                            hoveredSegment && hoveredSegment !== entry.id ? 'opacity(0.7)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        
        {/* Chart drop shadow effect */}
        <div className={styles.chartShadow} />
      </div>

      {/* Legend Section */}
      <div className={styles.legendSection}>
        <div className={styles.legendContainer}>
          {processedData.map((item, index) => {
            const studentCount = Math.round((item.value / 100) * actualTotalStudents);
            const isHidden = hiddenSegments.has(item.id);
            const isHovered = hoveredSegment === item.id;
            
            return (
              <div
                key={item.id}
                className={`${styles.legendItem} ${isHidden ? styles.legendItemHidden : ''} ${isHovered ? styles.legendItemHovered : ''}`}
                onClick={() => handleLegendClick(item.id)}
                onMouseEnter={() => setHoveredSegment(item.id)}
                onMouseLeave={() => setHoveredSegment(null)}
                role="button"
                tabIndex={0}
                aria-label={`${item.name}: ${item.value}%, ${studentCount} students. Click to ${isHidden ? 'show' : 'hide'}.`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLegendClick(item.id);
                  }
                }}
              >
                <div className={styles.legendIndicatorContainer}>
                  <div 
                    className={`${styles.legendIndicator} ${isHidden ? styles.legendIndicatorHidden : ''}`}
                    style={{ backgroundColor: item.color }}
                  />
                </div>
                <div className={styles.legendContent}>
                  <div className={styles.legendLabel}>{item.name}</div>
                  <div className={styles.legendStats}>
                    <span className={styles.legendPercentage}>{item.value}%</span>
                    <span className={styles.legendCount}>({studentCount.toLocaleString()} students)</span>
                  </div>
                </div>
                {isHidden && (
                  <div className={styles.hiddenIndicator} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                      <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Total students count - now shows actual student count */}
        <div className={styles.totalStudents}>
          <div className={styles.totalStudentsLabel}>Total Students:</div>
          <div className={styles.totalStudentsValue}>{actualTotalStudents.toLocaleString()}</div>
        </div>

        {/* Legend instructions for mobile */}
        <div className={styles.legendInstructions}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 17h.01" stroke="currentColor" strokeWidth="2"/>
          </svg>
          <span>Click legend items to show/hide segments</span>
        </div>
      </div>
    </div>
  );
};

const DetailedInfoTabs = ({ college, stats, cost }) => {
  const [activeTab, setActiveTab] = useState('admissions');
  const accentColor = getCollegeAccentColor(college.name);
  const lightAccentColor = getCollegeAccentColorLight(college.name);

  // Function to generate realistic aid percentage based on college characteristics
  const getRealisticAidPercentage = (collegeName, acceptanceRate, collegeType) => {
    const rate = parseFloat(acceptanceRate) || 50;
    const isPrivate = collegeType === 'Private';
    
    // More selective schools typically offer more aid
    // Private schools typically have higher aid percentages
    let basePercentage;
    
    if (rate < 10) { // Highly selective
      basePercentage = isPrivate ? 85 : 75;
    } else if (rate < 25) { // Very selective
      basePercentage = isPrivate ? 80 : 70;
    } else if (rate < 50) { // Selective
      basePercentage = isPrivate ? 75 : 65;
    } else { // Less selective
      basePercentage = isPrivate ? 70 : 60;
    }
    
    // Add some variation based on college name hash for consistency
    const nameHash = collegeName.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const variation = (nameHash % 10) - 5; // -5 to +4
    
    return Math.max(45, Math.min(95, basePercentage + variation));
  };

  const tabs = [
    { id: 'admissions', label: 'Admissions', icon: '📋' },
    { id: 'costs', label: 'Costs', icon: '💰' },
    { id: 'academics', label: 'Academics', icon: '📚' },
    { id: 'campus-life', label: 'Campus Life', icon: '🏫' }
  ];

  const getAdmissionsContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.detailSection}>
        <h4>Admission Requirements</h4>
        <ul className={styles.detailList}>
          <li>High school diploma or equivalent</li>
          <li>SAT/ACT scores (test-optional for many schools)</li>
          <li>Letters of recommendation</li>
          <li>Personal essay</li>
          <li>Extracurricular activities</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Application Process</h4>
        <ul className={styles.detailList}>
          <li>Common Application accepted</li>
          <li>Early Decision deadline: November 1</li>
          <li>Regular Decision deadline: {stats.applicationDeadline || 'January 1'}</li>
          <li>Rolling admissions available</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Selection Criteria</h4>
        <ul className={styles.detailList}>
          <li>Academic performance (GPA: {stats.gpa?.p25 || '3.5'}+ recommended)</li>
          <li>Standardized test scores</li>
          <li>Character and personal qualities</li>
          <li>Extracurricular record</li>
        </ul>
      </div>
    </div>
  );

  const getCostsContent = () => {
    const formatCurrency = (value) => {
      if (!value) return 'N/A';
      const num = typeof value === 'number' ? value : parseInt((value + '').replace(/[$,]/g, ''));
      if (isNaN(num)) return value;
      return `$${num.toLocaleString()}`;
    };

    return (
      <div className={styles.tabContent}>
        <div className={styles.detailSection}>
          <h4>Tuition & Fees</h4>
          <ul className={styles.detailList}>
            <li>Tuition: {formatCurrency(cost?.tuitionAndFees || '45,000')}</li>
            <li>Room & Board: {formatCurrency(cost?.roomAndBoard || '18,500')}</li>
            <li>Books & Supplies: $1,245</li>
            <li>Personal Expenses: $2,130</li>
          </ul>
        </div>

        <div className={styles.detailSection}>
          <h4>Financial Aid</h4>
          <ul className={styles.detailList}>
            <li>Students Receiving Aid: {cost?.studentsGetAid || `${getRealisticAidPercentage(college.name, stats.acceptanceRate, college.type)}%`}</li>
            <li>Average Need-Based Aid: {formatCurrency(cost?.averageAid || '58,755')}</li>
            <li>Average Merit-Based Aid: $12,500</li>
            <li>Students with No Loans: 65%</li>
          </ul>
        </div>

        <div className={styles.detailSection}>
          <h4>Payment Options</h4>
          <ul className={styles.detailList}>
            <li>Payment plans available</li>
            <li>Work-study opportunities</li>
            <li>Emergency financial assistance</li>
            <li>Scholarship opportunities</li>
          </ul>
        </div>
      </div>
    );
  };

  const getAcademicsContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.detailSection}>
        <h4>Academic Programs</h4>
        <ul className={styles.detailList}>
          <li>65+ undergraduate majors</li>
          <li>Graduate programs in 7 schools</li>
          <li>Interdisciplinary studies encouraged</li>
          <li>Research opportunities for undergraduates</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Academic Support</h4>
        <ul className={styles.detailList}>
          <li>Student-Faculty Ratio: {stats.studentFacultyRatio || '5:1'}</li>
          <li>Average Class Size: 18 students</li>
          <li>Academic tutoring available</li>
          <li>Writing and research support</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Graduation Outcomes</h4>
        <ul className={styles.detailList}>
          <li>4-Year Graduation Rate: {stats.fourYearGraduationRate || '75%'}%</li>
          <li>6-Year Graduation Rate: 94%</li>
          <li>Job Placement Rate: 90%</li>
          <li>Graduate School Acceptance: 85%</li>
        </ul>
      </div>
    </div>
  );

  const getCampusLifeContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.detailSection}>
        <h4>Student Life</h4>
        <ul className={styles.detailList}>
          <li>650+ student organizations</li>
          <li>36 varsity sports teams</li>
          <li>Residential college system</li>
          <li>Active Greek life (25% participation)</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Campus Facilities</h4>
        <ul className={styles.detailList}>
          <li>Multiple libraries and study spaces</li>
          <li>State-of-the-art fitness facilities</li>
          <li>Arts and cultural centers</li>
          <li>Innovation and maker spaces</li>
        </ul>
      </div>

      <div className={styles.detailSection}>
        <h4>Location & Environment</h4>
        <ul className={styles.detailList}>
          <li>Beautiful campus setting</li>
          <li>Close to major city opportunities</li>
          <li>Diverse and inclusive community</li>
          <li>Strong alumni network</li>
        </ul>
      </div>
    </div>
  );

  const getTabContent = () => {
    switch (activeTab) {
      case 'admissions':
        return getAdmissionsContent();
      case 'costs':
        return getCostsContent();
      case 'academics':
        return getAcademicsContent();
      case 'campus-life':
        return getCampusLifeContent();
      default:
        return getAdmissionsContent();
    }
  };

  return (
    <div className={styles.detailedInfoTabs}>
      <div className={styles.tabNavigation}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={activeTab === tab.id ? { 
              backgroundColor: lightAccentColor, 
              borderBottomColor: accentColor,
              color: accentColor 
            } : {}}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
      
      <div className={styles.tabContent} style={{ borderTopColor: accentColor }}>
        {getTabContent()}
      </div>
    </div>
  );
};

// --- Main Component ---

const CollegeProfile = () => {
  const { collegeName } = useParams();
  const location = useLocation();
  const navigate = useNavigate(); // Added useNavigate
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const [college, setCollege] = useState(null);
  const [isAdded, setIsAdded] = useState(false);
  const [myList, setMyList] = useState([]);
  const [toast, setToast] = useState(null);

  // Auto-save college list
  useAutoSave('myCollegeList', myList);

  useEffect(() => {
    const currentCollege = collegeData.find(c => c.name === collegeName);
    if (currentCollege) {
      setCollege(currentCollege);
      const savedList = JSON.parse(localStorage.getItem('myCollegeList')) || [];
      setMyList(savedList);
      if (savedList.some(c => c.name === currentCollege.name)) {
        setIsAdded(true);
      }
    }
  }, [collegeName]);

  if (!college) {
    return (
      <div className={styles.status}>
        College not found. We are expanding our list, please check back later!
      </div>
    );
  }
  
  const headerImage = getHeaderImage(college.name);
  const stats = college.stats || {};
  const demographics = stats.demographics || {};
  const cost = stats.cost || {};
  const accentColor = getCollegeAccentColor(college.name);

  const handleAddToList = async () => {
    if (!myList.some(c => c.name === college.name)) {
      // Create a simplified object for the list
      const collegeForList = {
        name: college.name,
        location: college.location,
        acceptanceRate: stats.acceptanceRate,
        satRange: stats.satRange,
        stats: stats // Include full stats for better compatibility
      };
      const updatedList = [...myList, collegeForList];
      setMyList(updatedList);
      setIsAdded(true);
      
      // Save to localStorage immediately
      localStorage.setItem('myCollegeList', JSON.stringify(updatedList));
      
      // Sync to backend if authenticated
      if (isAuthenticated && user) {
        try {
          const token = await getAccessTokenSilently();
          const { saveCollegeListToBackend } = await import('../utils/collegeListSync');
          // Use relative URL - will go through Amplify proxy to backend
          const apiBaseUrl = process.env.REACT_APP_EXPRESS_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '';
          await saveCollegeListToBackend(updatedList, token, apiBaseUrl);
          console.log('[CollegeProfile] Saved college list to backend');
        } catch (error) {
          console.error('[CollegeProfile] Error saving to backend:', error);
          // Don't fail - localStorage save succeeded
        }
      }
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('collegeListUpdated'));
      
      // Show success toast
      setToast({
        message: `${college.name} added to your list!`,
        type: 'success'
      });
    }
  };

  const closeToast = () => {
    setToast(null);
  };

  const handleReturnToExplore = () => {
    // Get the stored scroll data, if any
    const scrollDataStr = sessionStorage.getItem('exploreScrollPosition');
    
    // Navigate back to explore page (fixing route mismatch)
    navigate('/explore');
    
    // Restore scroll position after navigation if available
    if (scrollDataStr) {
      try {
        const scrollData = JSON.parse(scrollDataStr);
        
        // Validate scroll data (check if it's recent and from explore page)
        const isRecent = Date.now() - scrollData.timestamp < 300000; // 5 minutes
        const isFromExplorePage = scrollData.path && scrollData.path.includes('/explore');
        
        if (scrollData.position !== undefined && isRecent && isFromExplorePage) {
          // Use requestAnimationFrame for better timing
          requestAnimationFrame(() => {
            setTimeout(() => {
              window.scrollTo({
                top: scrollData.position,
                behavior: 'smooth'
              });
              // Clean up the stored position
              sessionStorage.removeItem('exploreScrollPosition');
            }, 200); // Increased timeout for better reliability
          });
        } else {
          // Clean up invalid/old scroll data
          sessionStorage.removeItem('exploreScrollPosition');
        }
      } catch (error) {
        // Handle invalid JSON, clean up
        sessionStorage.removeItem('exploreScrollPosition');
      }
    }
  };

  return (
    <div className={styles.profilePage}>
      <button 
        onClick={handleReturnToExplore}
        className={styles.returnBtn}
      >
        ← Explore
      </button>
      
      <header className={styles.header}>
        <img src={headerImage} alt={`${college.name} campus`} className={styles.collegeLogo} />
        <div className={styles.headerText}>
          <h1>{college.name}</h1>
          <p className={styles.location}>{college.location || 'Location N/A'}</p>
          {college.type && <p className={styles.collegeType} style={{ color: accentColor }}>{college.type}</p>}
        </div>
        <button 
          onClick={handleAddToList} 
          className={`${styles.addToListBtn} ${isAdded ? styles.added : ''}`}
          disabled={isAdded}
        >
          {isAdded ? '✓ Added' : '+ Add to List'}
        </button>
      </header>

      <main className={styles.newMainContent}>
        <div className={styles.leftColumn}>
          <AcceptanceCard acceptanceRate={stats.acceptanceRate} college={college} />
          <CostsCard cost={cost} stats={stats} college={college} />
          <WhyStudentsChooseCard college={college} />
          
          <InfoCard title="Race & Ethnicity Diversity">
            {demographics.raceEthnicity ? (
              <>
                <DemographicsPieChart 
                  data={demographics.raceEthnicity} 
                  totalStudents={parseInt((stats.studentCount || '5200').replace(/,/g, ''))}
                />
                <div className={styles.diversityActions}>
                  <button 
                    className={styles.actionButton}
                    onClick={() => {
                      // Open visit scheduling in new tab (could be school's visit page)
                      window.open(`https://${college.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.edu/visit`, '_blank');
                    }}
                  >
                    Schedule Visit
                  </button>
                  <button 
                    className={`${styles.actionButton} ${isAdded ? styles.added : ''}`}
                    onClick={handleAddToList}
                    disabled={isAdded}
                  >
                    {isAdded ? '✓ Added' : '+ Add to List'}
                  </button>
                  <button 
                    className={styles.actionButton}
                    onClick={() => {
                      // Open admissions/request info page
                      window.open(`https://${college.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.edu/admissions/request-info`, '_blank');
                    }}
                  >
                    Request Info
                  </button>
                </div>
              </>
            ) : (
              <p>Race & ethnicity data not available.</p>
            )}
          </InfoCard>
        </div>
        
        <div className={styles.rightColumn}>
          <QuickFactsCard college={college} stats={stats} cost={cost} />
        </div>
      </main>

      <div className={styles.fullWidthSection}>
        <DetailedInfoTabs college={college} stats={stats} cost={cost} />
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CollegeProfile; 
